import React, { useMemo, useState } from "react";
import { Card, Space, Tag, Tooltip, Typography } from "antd";
import { CopyOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FileTypeIcon from "./FileTypeIcon";
import {
  formatFileSize,
  formatShortDate,
  getAiFlags,
  getParseDashboardUrl,
  isImage,
} from "./allegatiUtils";
import { getThumbnailUrl } from "../../../common/utils/thumbnails";
import ParseLogo from "../../../shared/images/parse.png";

const { Text } = Typography;

// Scacchiera per rendere leggibili le immagini con trasparenza.
const CHECKERBOARD =
  "repeating-conic-gradient(#f0f0f0 0% 25%, transparent 0% 50%) 50% / 16px 16px";

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: 16,
};

const coverStyle = {
  position: "relative",
  aspectRatio: "1 / 1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  borderRadius: "8px 8px 0 0",
  cursor: "pointer",
};

const overlayStyle = (visible) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  background: "rgba(0, 0, 0, 0.55)",
  opacity: visible ? 1 : 0,
  transition: "opacity 0.2s ease-in-out",
  pointerEvents: visible ? "auto" : "none",
});

const overlayIconStyle = { color: "#fff", fontSize: 18 };

const AllegatoCard = ({ item, isProd, onFileAction }) => {
  const [hovered, setHovered] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  const fileUrl = item.allegato?.url;

  // Memoizzato: senza, ogni hover ricostruirebbe l'EdgeResizer di tutte le card toccate.
  const thumbnailUrl = useMemo(
    () => (isImage(item.mimeType) ? getThumbnailUrl(fileUrl, isProd) : ""),
    [item.mimeType, fileUrl, isProd]
  );

  const showThumbnail = Boolean(thumbnailUrl) && !thumbnailFailed;
  const aiFlags = useMemo(() => getAiFlags(item), [item]);

  // Le azioni sull'overlay non devono far scattare anche il click sul cover.
  const overlayAction = (action) => (event) => {
    event.stopPropagation();
    onFileAction(item, action);
  };

  return (
    <Card
      size="small"
      hoverable
      styles={{ body: { padding: 10 } }}
      cover={
        <div
          style={{
            ...coverStyle,
            background: showThumbnail ? CHECKERBOARD : "#fafafa",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => onFileAction(item, "view")}
        >
          {showThumbnail ? (
            <LazyLoadImage
              src={thumbnailUrl}
              alt={item.nome || item.objectId}
              onError={() => setThumbnailFailed(true)}
              wrapperProps={{ style: { width: "100%", height: "100%" } }}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <FileTypeIcon mimeType={item.mimeType} size={48} />
          )}

          <div style={overlayStyle(hovered)}>
            {fileUrl && (
              <>
                <Tooltip title="Visualizza">
                  <EyeOutlined style={overlayIconStyle} onClick={overlayAction("view")} />
                </Tooltip>
                <Tooltip title="Copia URL">
                  <CopyOutlined style={overlayIconStyle} onClick={overlayAction("copy")} />
                </Tooltip>
                <Tooltip title="Scarica">
                  <DownloadOutlined style={overlayIconStyle} onClick={overlayAction("download")} />
                </Tooltip>
              </>
            )}
            <Tooltip title="Apri in Parse Dashboard">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={getParseDashboardUrl(item.objectId, isProd)}
                onClick={(event) => event.stopPropagation()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#fff",
                  borderRadius: 4,
                  padding: 3,
                }}
              >
                <img src={ParseLogo} width={16} alt="Parse Dashboard" />
              </a>
            </Tooltip>
          </div>
        </div>
      }
    >
      <Tooltip title={item.nome || "Senza nome"}>
        <Text ellipsis style={{ display: "block", fontWeight: 500 }}>
          {item.nome || "Senza nome"}
        </Text>
      </Tooltip>

      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
        {formatFileSize(item.size_bytes)} · {formatShortDate(item.createdAt)}
      </Text>

      {/* Altezza fissa anche senza badge, così le card restano allineate. */}
      <div style={{ minHeight: 30, marginTop: 6 }}>
        <Space size={4} wrap>
          {item.condiviso && <Tag color="green">Condiviso</Tag>}
          {item.provino && <Tag color="blue">Provino</Tag>}
          {aiFlags.length > 0 && (
            <Tooltip title={aiFlags.join(", ")}>
              <Tag color="purple">AI</Tag>
            </Tooltip>
          )}
        </Space>
      </div>
    </Card>
  );
};

const AllegatiGrid = ({ items, isProd, onFileAction }) => (
  <div style={gridStyle}>
    {items.map((item) => (
      <AllegatoCard
        key={item.objectId}
        item={item}
        isProd={isProd}
        onFileAction={onFileAction}
      />
    ))}
  </div>
);

export default AllegatiGrid;
